import { FC, useEffect, useRef, useState } from 'react'
import { Form, Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form';
import { COLLECTIONS, LOCAL_STORAGE_KEYS, PRIVACY_POLICY_URL } from '../utils/shared/constants';
import { AdvancedButton } from '../components/AdvancedButton';
import useQueryParam from '../hooks/useQueryParams';
import { useText } from '../context/TextContext';
import BrandName from '../components/BrandName';
import { useFirebaseDoc } from '../hooks/firebaseHooks';
import { UserInvitation } from '../models/shared/UserInvitation';
import OrganizationPublicBrand from '../components/OrganizationPublicBrand';
import { loginInit, loginVerifyWithCode } from '../utils/api';
import { useAuth } from 'reactfire';
import { GoogleAuthProvider, FacebookAuthProvider, signInWithPopup, signInWithCustomToken } from 'firebase/auth';
import { toast } from 'react-toastify';
import { getBrowserLanguage } from '../utils/presentation';
import config from '../utils/config';

interface IProps {
    isInvite?: boolean
}

/**
* @author
* @function @LoginPage
**/
type Inputs = {
    email: string
};

export const LoginPage: FC<IProps> = (props) => {
    const { orgId, id, state } = useQueryParam();
    const [invite] = useFirebaseDoc<UserInvitation>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.USER_INVITATIONS}/${id}`);
    const texts = useText();

    return (

        <div className='center flex-1'>
            <div className='d-none d-md-flex  border border-dark rounded flex-1 m-3' style={{ minHeight: 600, maxWidth: 1200 }}>
                <div className='col-md-6 bg-dark text-light p-3 d-flex flex-column justify-content-between'>
                    <BrandName light size='lg' />

                    <div className='my-4'>
                        <p className='fw-bold mb-3'>{texts.loginPageHeadline}</p>
                        <ul className='list-unstyled mb-3'>
                            <li>✓ {texts.loginPageFeature1}</li>
                            <li>✓ {texts.loginPageFeature2}</li>
                            <li>✓ {texts.loginPageFeature3}</li>
                            <li>✓ {texts.loginPageFeature4}</li>
                            <li>✓ {texts.loginPageFeature5}</li>
                        </ul>
                        <p className='mb-0 text-light-50 small'>{config.isNativeApp ? texts.loginPageGetStartedEmailOnly : texts.loginPageGetStarted}</p>
                    </div>

                    <div className='d-flex justify-content-end'>
                        <p className='mb-0'>{texts.brandDescription}</p>
                    </div>
                </div>

                <div className='col-md-6 flex-1 py-3 center flex-column gap-4'>
                    {props.isInvite && <OrganizationPublicBrand />}

                    <LoginForm state={state} invite={invite} isInvite={props.isInvite} />
                </div>
            </div>

            <div className='h-100 w-100 d-md-none d-flex flex-column'>
                <div className='bg-dark text-light p-3 w-100 d-flex justify-content-between'>
                    <BrandName light size='lg' />
                </div>
                <div className='d-flex flex-column align-items-center gap-4 mt-4'>
                    {props.isInvite && <OrganizationPublicBrand />}
                    <div className='px-3 text-center'>
                        <p className='fw-bold mb-2'>{texts.loginPageHeadline}</p>
                        <p className='text-muted small mb-0'>{config.isNativeApp ? texts.loginPageMobileGetStartedEmailOnly : texts.loginPageMobileGetStarted}</p>
                    </div>
                    <LoginForm state={state} invite={invite} isInvite={props.isInvite} />
                </div>
            </div>
        </div>
    )
}


interface LoginFormProps {
    state: string
    invite: UserInvitation | null
    isInvite?: boolean
}

const LoginForm = (props: LoginFormProps) => {
    const [emailSent, setEmailSent] = useState(false);
    const { register, handleSubmit, watch, setValue } = useForm<Inputs>();
    const [processing, setProcessing] = useState(false);
    const [googleProcessing, setGoogleProcessing] = useState(false);
    const [facebookProcessing, setFacebookProcessing] = useState(false);
    const texts = useText();
    const auth = useAuth();

    useEffect(() => {
        if (props.invite?.email) {
            setValue('email', props.invite.email);
        }
    }, [props.invite, setValue])

    const hasEmail = !!watch('email');

    const handleLogin = async (data: Inputs) => {
        setProcessing(true);

        window.localStorage.setItem(LOCAL_STORAGE_KEYS.MAGIC_LINK_EMAIL, data.email);
        await loginInit(data.email);
        setEmailSent(true);
        setProcessing(false);
    }

    const handleGoogleLogin = async () => {
        setGoogleProcessing(true);
        try {
            await signInWithPopup(auth, new GoogleAuthProvider());
        } finally {
            setGoogleProcessing(false);
        }
    }

    const handleFacebookLogin = async () => {
        setFacebookProcessing(true);
        try {
            await signInWithPopup(auth, new FacebookAuthProvider());
        } finally {
            setFacebookProcessing(false);
        }
    }


    if (emailSent) {
        return <LoginCodeForm email={watch('email')} />
    }

    let label = texts.login;
    if (props.isInvite) {
        label = texts.acceptInvitation;
    } else if (props.state === 'signup') {
        label = texts.signUp;
    }

    return <div className='d-flex flex-column gap-3'>
        <Form onSubmit={handleSubmit(handleLogin)}>
            <Form.Group className="mb-3 d-flex flex-column gap-2">
                <Form.Label className='m-0'>{texts.emailAddress}</Form.Label>
                <Form.Control className='m-0' type="email" placeholder={texts.enterEmail} {...register('email')} readOnly={props.isInvite} />
                <AdvancedButton processing={processing} className='' variant="dark" type="submit" disabled={!hasEmail}>{label}</AdvancedButton>
            </Form.Group>
        </Form>
        {/* Popup sign-in doesn't work inside the native app's WebView */}
        {!props.isInvite && !config.isNativeApp && (
            <>
                <div className='d-flex align-items-center gap-2'>
                    <hr className='flex-1 m-0' />
                    <span className='text-muted small'>{texts.orDivider}</span>
                    <hr className='flex-1 m-0' />
                </div>
                <AdvancedButton processing={googleProcessing} variant="outline-dark" onClick={handleGoogleLogin}>
                    {texts.signInWithGoogle}
                </AdvancedButton>
                <AdvancedButton processing={facebookProcessing} variant="outline-dark" onClick={handleFacebookLogin}>
                    {texts.signInWithFacebook}
                </AdvancedButton>
            </>
        )}
        <a href={PRIVACY_POLICY_URL} target='_blank' rel='noreferrer' className='small text-muted text-center'>{texts.privacyPolicy}</a>
    </div>
}


const LOGIN_CODE_LENGTH = 6;
const EMPTY_CODE = Array<string>(LOGIN_CODE_LENGTH).fill('');

const LoginCodeForm = (props: { email: string }) => {
    const [digits, setDigits] = useState<string[]>(EMPTY_CODE);
    const [processing, setProcessing] = useState(false);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const texts = useText();
    const auth = useAuth();

    const code = digits.join('');

    useEffect(() => {
        if (code.length !== LOGIN_CODE_LENGTH || processing) {
            return;
        }

        const verify = async () => {
            setProcessing(true);
            try {
                const result = await loginVerifyWithCode(props.email, code, getBrowserLanguage());
                await signInWithCustomToken(auth, result.customToken);
            } catch (e) {
                console.error('Failed to verify login code', e);
                toast.error(texts.invalidLoginCode);
                setDigits(EMPTY_CODE);
                setProcessing(false);
                inputRefs.current[0]?.focus();
            }
        }
        verify();
    }, [code, processing, props.email, auth, texts.invalidLoginCode]);

    // Fills digits starting at `index`, so pasting or OS autofill of the whole code works from any box
    const fillFrom = (index: number, value: string) => {
        const newDigits = value.replace(/\D/g, '').slice(0, LOGIN_CODE_LENGTH - index).split('');
        if (newDigits.length === 0) {
            return;
        }
        const next = [...digits];
        for (let offset = 0; offset < newDigits.length; offset++) {
            next[index + offset] = newDigits[offset];
        }
        setDigits(next);
        inputRefs.current[Math.min(index + newDigits.length, LOGIN_CODE_LENGTH - 1)]?.focus();
    }

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            const next = [...digits];
            if (next[index]) {
                next[index] = '';
            } else if (index > 0) {
                next[index - 1] = '';
                inputRefs.current[index - 1]?.focus();
            }
            setDigits(next);
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < LOGIN_CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    }

    return <div className='center flex-column'>
        <p>{texts.emailSentTo} <span className='text-bold'>{props.email}</span>!</p>
        <p className='text-muted'>{texts.checkSpamFolder}</p>
        <p className='text-muted'>{texts.loginCodeInstructions}</p>
        <Form.Label className='m-0 mb-2'>{texts.loginCode}</Form.Label>
        <div className='d-flex gap-2 align-items-center'>
            {digits.map((digit, index) => (
                <Form.Control
                    key={index}
                    ref={(el: HTMLInputElement | null) => { inputRefs.current[index] = el; }}
                    className='text-center fs-4 p-0'
                    style={{ width: 44, height: 52 }}
                    inputMode='numeric'
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    aria-label={`${texts.loginCode} ${index + 1}`}
                    value={digit}
                    disabled={processing}
                    autoFocus={index === 0}
                    onFocus={e => e.target.select()}
                    onKeyDown={e => handleKeyDown(index, e as React.KeyboardEvent<HTMLInputElement>)}
                    onChange={e => fillFrom(index, e.target.value.replace(digit, '') || e.target.value)}
                    onPaste={e => { e.preventDefault(); fillFrom(index, e.clipboardData.getData('text')); }}
                />
            ))}
            {processing && <Spinner size='sm' className='ms-2' />}
        </div>
    </div>
}
